import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import {
  ApiOutlined,
  LinkOutlined,
  ShopOutlined,
  BookOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  BankOutlined,
  LogoutOutlined,
  UserOutlined,
  DownOutlined,
  KeyOutlined,
  ClusterOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import { useT } from '../i18n';
import { brand, colors, glassDivider, glassPanelStyle } from '../theme';
import BrandLogomark from './common/BrandLogomark';
import BrainIndexingIndicator from './common/BrainIndexingIndicator';
import RoleBadge from './common/RoleBadge';
import { MCP_SERVERS } from '../types/mcp';

const MCP_SIDEBAR_PREVIEW_COUNT = 3;

// ── Types ─────────────────────────────────────────────────────────────────────

interface NavItem {
  id: string;
  labelKey: string;
  path: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

interface NavSection {
  sectionKey: string;
  items: NavItem[];
}

// ── Nav structure ─────────────────────────────────────────────────────────────

const NAV: NavSection[] = [
  {
    sectionKey: 'nav.section.agentic',
    items: [
      { id: 'agents',      labelKey: 'nav.agents',      path: '/',            icon: <ApiOutlined /> },
      { id: 'connectors',  labelKey: 'nav.connectors',  path: '/connectors',  icon: <LinkOutlined /> },
      { id: 'marketplace', labelKey: 'nav.marketplace', path: '/marketplace', icon: <ShopOutlined /> },
      { id: 'playground',  labelKey: 'nav.playground',  path: '/playground',  icon: <ThunderboltOutlined /> },
    ],
  },
  {
    sectionKey: 'nav.section.organization',
    items: [
      { id: 'brain',       labelKey: 'nav.brain',       path: '/brain',               icon: <BookOutlined /> },
      { id: 'tokens',      labelKey: 'nav.tokens',      path: '/organization/tokens', icon: <KeyOutlined />, adminOnly: true },
      { id: 'mcps',        labelKey: 'nav.mcps',        path: '/organization/mcp',    icon: <ClusterOutlined />, adminOnly: true },
    ],
  },
];

const BOTTOM_LINKS: NavItem[] = [];

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(email: string | null): string {
  if (!email) return '?';
  const name = email.split('@')[0];
  const parts = name.split('.');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function useActiveId(): string {
  const { pathname } = useLocation();
  if (pathname.startsWith('/marketplace')) return 'marketplace';
  if (pathname.startsWith('/brain'))                    return 'brain';
  if (pathname.startsWith('/organization/tokens'))      return 'tokens';
  if (pathname.startsWith('/organization/mcp'))         return 'mcps';
  if (pathname.startsWith('/connectors'))               return 'connectors';
  if (pathname.startsWith('/playground'))   return 'playground';
  if (pathname.startsWith('/roadmap'))      return 'roadmap';
  if (pathname.startsWith('/docs') && !pathname.startsWith('/api-docs')) return 'docs';
  if (pathname.startsWith('/api-docs'))     return 'apiDocs';
  if (pathname.startsWith('/changelog'))    return 'changelog';
  if (pathname === '/' || pathname.startsWith('/builder')) return 'agents';
  return '';
}

// ── Sub-components ────────────────────────────────────────────────────────────

const NavBtn: React.FC<{
  item: NavItem;
  active: boolean;
  onClick: () => void;
  t: (k: string) => string;
}> = ({ item, active, onClick, t }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      width: '100%', padding: '6px 10px', borderRadius: 6,
      fontSize: 13, fontWeight: active ? 600 : 400,
      cursor: 'pointer', border: 'none', textAlign: 'left',
      color: active ? colors.primary : '#4b5563',
      background: active ? colors.primarySoft : 'transparent',
      transition: 'background 0.12s, color 0.12s',
    }}
    onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
    onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
  >
    <span style={{ fontSize: 13, flexShrink: 0 }}>{item.icon}</span>
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {t(item.labelKey)}
    </span>
  </button>
);

/** One row inside the expanded MCPs sub-list — a preview server or the
 * "+N más" overflow link. Shared so the hover highlight exists once. */
const SidebarSubItem: React.FC<{
  label: React.ReactNode;
  color?: string;
  fontWeight?: number;
  onClick: () => void;
}> = ({ label, color = '#4b5563', fontWeight = 400, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', width: '100%', padding: '5px 10px', borderRadius: 6,
      fontSize: 12, fontWeight, cursor: 'pointer', border: 'none', textAlign: 'left',
      color, background: 'transparent', transition: 'background 0.12s',
    }}
    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
  >
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
  </button>
);

/**
 * "MCPs" nav entry with a toggle chevron: expands to the first
 * MCP_SIDEBAR_PREVIEW_COUNT servers (each jumps straight to its own detail
 * page) plus a "+N más" row when there are more, which — like the main
 * label — goes to the full Servidores MCP list.
 */
const McpNavItem: React.FC<{
  active: boolean;
  onNavigate: (path: string) => void;
  t: (k: string) => string;
}> = ({ active, onNavigate, t }) => {
  const [expanded, setExpanded] = useState(false);
  const preview = MCP_SERVERS.slice(0, MCP_SIDEBAR_PREVIEW_COUNT);
  const moreCount = MCP_SERVERS.length - preview.length;

  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', width: '100%', borderRadius: 6,
          color: active ? colors.primary : '#4b5563',
          background: active ? colors.primarySoft : 'transparent',
          transition: 'background 0.12s, color 0.12s',
        }}
        onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = '#f3f4f6'; }}
        onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
      >
        <button
          onClick={() => onNavigate('/organization/mcp')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0,
            padding: '6px 0 6px 10px', fontSize: 13, fontWeight: active ? 600 : 400,
            cursor: 'pointer', border: 'none', background: 'transparent', textAlign: 'left', color: 'inherit',
          }}
        >
          <span style={{ fontSize: 13, flexShrink: 0 }}><ClusterOutlined /></span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t('nav.mcps')}
          </span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          aria-label={expanded ? 'Colapsar servidores MCP' : 'Expandir servidores MCP'}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, marginRight: 4, flexShrink: 0,
            border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit',
          }}
        >
          <RightOutlined style={{ fontSize: 9, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }} />
        </button>
      </div>

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 1, marginLeft: 10, paddingLeft: 10, borderLeft: glassDivider }}>
          {preview.map((server) => (
            <SidebarSubItem
              key={server.id}
              label={server.name}
              onClick={() => onNavigate(`/organization/mcp/${server.id}`)}
            />
          ))}
          {moreCount > 0 && (
            <SidebarSubItem
              label={`+${moreCount} más`}
              color={colors.primary}
              fontWeight={500}
              onClick={() => onNavigate('/organization/mcp')}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ── AppSidebar ────────────────────────────────────────────────────────────────

const AppSidebar: React.FC = () => {
  const navigate = useNavigate();
  const activeId = useActiveId();
  const { email, role, isAdmin, logout } = useUser();
  const t = useT();

  const profileMenu: MenuProps = {
    items: [
      {
        key: 'info',
        label: (
          <div style={{ padding: '4px 0', minWidth: 180 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#262626' }}>{email}</div>
            <div style={{ marginTop: 4 }}>
              <RoleBadge role={role} variant="chip" />
            </div>
          </div>
        ),
        disabled: true,
      },
      { type: 'divider' },
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: t('nav.section.settings'),
        onClick: () => navigate('/settings'),
      },
      ...(isAdmin ? [{
        key: 'organization',
        icon: <BankOutlined />,
        label: t('user.menu.organization'),
        onClick: () => navigate('/organization'),
      }] : []),
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('user.menu.logout'),
        danger: true,
        onClick: logout,
      },
    ],
  };

  return (
    <div style={{
      ...glassPanelStyle,
      width: 220,
      flexShrink: 0,
      height: 'calc(100vh - 24px)',
      margin: '12px 0 12px 12px',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      overflowX: 'hidden',
    }}>

      {/* ── Brand ── */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '14px 12px 10px', flexShrink: 0 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', minWidth: 0 }}
          onClick={() => navigate('/')}
        >
          <BrandLogomark size={26} />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#141414', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {brand.name}
          </span>
        </div>
        {/* Spinner while the Brain is being updated in the background; click for details. */}
        <BrainIndexingIndicator placement="rightTop" />
      </div>

      {/* ── Nav sections ── */}
      <div style={{ flex: 1, padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {NAV.map((section) => (
          <div key={section.sectionKey} style={{ marginBottom: 4 }}>
            <div style={{
              fontSize: 10, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase',
              color: '#bfbfbf', padding: '6px 10px 3px',
            }}>
              {t(section.sectionKey)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {section.items.filter(item => !item.adminOnly || isAdmin).map((item) => (
                item.id === 'mcps' ? (
                  <McpNavItem
                    key={item.id}
                    active={activeId === item.id}
                    onNavigate={navigate}
                    t={t}
                  />
                ) : (
                  <NavBtn
                    key={item.id}
                    item={item}
                    active={activeId === item.id}
                    onClick={() => navigate(item.path)}
                    t={t}
                  />
                )
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Bottom links ── */}
      <div style={{ padding: '4px 8px', borderTop: glassDivider, flexShrink: 0 }}>
        {BOTTOM_LINKS.map((item) => (
          <NavBtn
            key={item.id}
            item={item}
            active={activeId === item.id}
            onClick={() => navigate(item.path)}
            t={t}
          />
        ))}
      </div>

      {/* ── User profile ── */}
      <div style={{ padding: '8px', borderTop: glassDivider, flexShrink: 0 }}>
        <Dropdown menu={profileMenu} trigger={['click']} placement="topLeft">
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
            transition: 'background 0.12s',
          }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.background = '#f3f4f6')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.background = 'transparent')}
          >
            <Avatar
              size={26}
              style={{
                background: colors.primary,
                fontSize: 10, fontWeight: 700, flexShrink: 0,
              }}
              icon={!email ? <UserOutlined /> : undefined}
            >
              {email ? initials(email) : undefined}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#262626', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {email?.split('@')[0]}
              </div>
              <RoleBadge role={role} variant="chip" />
            </div>
            <DownOutlined style={{ fontSize: 9, color: '#bfbfbf', flexShrink: 0 }} />
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

export default AppSidebar;
